/**
 * Cable profiles -- group 03.
 *
 * Tellurium Q, Black Rhodium, Ecosse Cables, Townshend Audio, Furutech, Oyaide,
 * Acrolink and Zonotone: four British boutiques and four Japanese wire houses.
 *
 * NOT ONE of these eight manufacturers publishes loop resistance, inductance
 * and capacitance per metre for its finished cables. Several publish conductor
 * cross-section and conductor metal, which is a genuine fact and is used here;
 * none publish R, L and C. So every entry in this file omits the electrical
 * fields and lets the kit derive them from gauge, material and geometry, and no
 * entry is marked MANUFACTURER_SPEC on its electrical figures. Where the gauge
 * or the geometry itself had to be inferred from the cable\'s physical form, the
 * provenance note says so rather than letting the default text imply a
 * manufacturer statement that does not exist.
 *
 * Tellurium Q is the extreme case: it publishes no conductor material, no
 * gauge, no geometry and no measurements for anything in the range, as a stated
 * policy. Its entries are therefore inference from the outside of the cable,
 * flagged as such, and carry ESTIMATED confidence throughout.
 *
 * Power, USB and Ethernet entries describe construction only. The engine
 * applies no signal processing to those three types, and nothing here is
 * written to imply otherwise, however expensive the cord.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

const TQ_UNDISCLOSED =
  'Tellurium Q publishes no conductor material, gauge, geometry, dielectric or electrical data for any product in its range, as a stated policy. Conductor, geometry and gauge here are inferred from the external form of the cable and from the connectors it ships with, not from a manufacturer statement, and the derived electrical figures inherit that uncertainty.';

export const CABLES_03: CableProfile[] = [
  // =========================================================================
  // Tellurium Q -- Cornwall, United Kingdom
  // =========================================================================
  makeCable({
    mfr: 'Tellurium Q',
    model: 'Blue II Speaker Cable',
    cableType: 'speaker',
    price: usd(430, 'APPROXIMATE', 2025, 'Approximate price for a 2.5 m terminated pair; sold by length, so the total moves with length and termination.'),
    priceLengthM: 2.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 16,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#3b82c4',
    tags: ['flat-jacket', 'undisclosed-construction', 'entry-range'],
    provenanceNote: TQ_UNDISCLOSED + ' The range name is not a material statement; OFC is assumed as the commonest case for a cable at this price, not because the maker says so.',
    notes:
      'A wide, flat two-conductor jacket, which is the one construction fact visible from the outside. Treated as a flat-parallel pair at about 16 AWG, loop resistance works out near 0.026 ohm/m, so a 2.5 m run puts roughly 0.066 ohm in series with the loudspeaker -- about 0.14 dB of level loss into a 4 ohm minimum, and more where the impedance dips lower. The close, wide spacing that makes it flat is also what keeps inductance low and pushes capacitance up.',
  }),

  makeCable({
    mfr: 'Tellurium Q',
    model: 'Black II Speaker Cable',
    cableType: 'speaker',
    price: usd(900, 'APPROXIMATE', 2025, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 15,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#3a3f4a',
    tags: ['flat-jacket', 'undisclosed-construction'],
    provenanceNote: TQ_UNDISCLOSED,
    notes:
      'Physically a heavier version of the same flat jacket. At an inferred 15 AWG the loop resistance is about 0.021 ohm/m, so 2.5 m adds roughly 0.052 ohm and costs about 0.11 dB into 4 ohms. The maker describes its design goal in terms of phase behaviour but publishes no figure for it, so nothing of that kind is encoded here.',
  }),

  makeCable({
    mfr: 'Tellurium Q',
    model: 'Ultra Black II Speaker Cable',
    cableType: 'speaker',
    price: usd(1900, 'APPROXIMATE', 2025, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 14,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#2f333c',
    tags: ['flat-jacket', 'undisclosed-construction'],
    provenanceNote: TQ_UNDISCLOSED,
    notes:
      'The wider flat jacket of the Ultra Black line. Inferred at 14 AWG, loop resistance is near 0.017 ohm/m: 0.041 ohm over 2.5 m, or about 0.09 dB into 4 ohms. The step from the Black II is a conductor-area step of roughly 25 per cent, which is what the arithmetic above reflects and is the only part of the difference this database can account for.',
  }),

  makeCable({
    mfr: 'Tellurium Q',
    model: 'Silver Diamond Speaker Cable',
    cableType: 'speaker',
    price: usd(8000, 'APPROXIMATE', 2025, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 13,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#b9c0c8',
    tags: ['flat-jacket', 'undisclosed-construction', 'upper-range'],
    provenanceNote: TQ_UNDISCLOSED + ' In particular, the word Silver in the range name has never been confirmed by the maker as a statement about the conductor metal, so the conservative OFC assumption is used rather than reading the marketing name as a specification.',
    notes:
      'Flat jacket again, heavier still. At an inferred 13 AWG that is about 0.013 ohm/m of loop resistance and roughly 0.033 ohm over 2.5 m, near 0.07 dB into 4 ohms. Between this and the Ultra Black II the engine can only act on that conductor-area difference, which at these lengths is a few hundredths of a decibel.',
  }),

  makeCable({
    mfr: 'Tellurium Q',
    model: 'Statement Speaker Cable',
    cableType: 'speaker',
    price: usd(15000, 'APPROXIMATE', 2025, 'Approximate price for a 2.5 m terminated pair; the maker quotes per installation.'),
    priceLengthM: 2.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8b6f3e',
    tags: ['flat-jacket', 'undisclosed-construction', 'top-of-range'],
    provenanceNote: TQ_UNDISCLOSED + ' The price is an approximation from dealer listings and varies widely with length and termination.',
    notes:
      'The largest flat section in the range, inferred here at 12 AWG. Loop resistance near 0.010 ohm/m gives about 0.026 ohm over 2.5 m, roughly 0.06 dB into 4 ohms. That is the entire modelled difference between this cable and the entry Blue II: about a tenth of a decibel, on a cable costing thirty times as much.',
  }),

  makeCable({
    mfr: 'Tellurium Q',
    model: 'Blue II RCA Interconnect',
    cableType: 'interconnect',
    price: usd(260, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 22,
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#3b82c4',
    tags: ['flat-jacket', 'undisclosed-construction', 'entry-range'],
    provenanceNote: TQ_UNDISCLOSED,
    notes:
      'Flat jacket like the speaker cables, so it is modelled as a flat-parallel pair, which is the high-capacitance end of the geometry range at roughly 300 pF/m. A 1 m pair therefore presents around 300 pF: against a 600 ohm valve output stage the low-pass corner lands near 880 kHz, and against a typical 100 ohm solid-state output near 5 MHz. Either way the in-band result is nil, which is what the engine will report.',
  }),

  makeCable({
    mfr: 'Tellurium Q',
    model: 'Black II RCA Interconnect',
    cableType: 'interconnect',
    price: usd(620, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 22,
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#3a3f4a',
    tags: ['flat-jacket', 'undisclosed-construction'],
    provenanceNote: TQ_UNDISCLOSED,
    notes:
      'Same external form as the Blue II interconnect in the Black line jacket. Capacitance is what matters for an interconnect and the flat geometry keeps it comparatively high; on a 1 m run from any normal source impedance the computed in-band loss is a small fraction of a hundredth of a decibel.',
  }),

  makeCable({
    mfr: 'Tellurium Q',
    model: 'Ultra Black II XLR Interconnect',
    cableType: 'interconnect',
    price: usd(1400, 'APPROXIMATE', 2025, 'Approximate price for a 1 m balanced pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#2f333c',
    tags: ['balanced', 'undisclosed-construction'],
    provenanceNote: TQ_UNDISCLOSED + ' Twisted-pair geometry is inferred from the fact that a balanced XLR link requires two signal conductors of equal impedance to earth, which is what the connector demands rather than what the maker states.',
    notes:
      'A balanced version of the Ultra Black line. A twisted pair sits mid-range for both inductance and capacitance, around 70 pF/m, so a 1 m link is about 70 pF and the corner against a 600 ohm balanced output is several megahertz up. The practical benefit of the balanced connection is common-mode rejection at the receiver, which is a property of the receiving input stage and not of the cable.',
  }),

  makeCable({
    mfr: 'Tellurium Q',
    model: 'Silver Diamond RCA Interconnect',
    cableType: 'interconnect',
    price: usd(5000, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 22,
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#b9c0c8',
    tags: ['flat-jacket', 'undisclosed-construction', 'upper-range'],
    provenanceNote: TQ_UNDISCLOSED + ' As with the speaker cable of the same name, the conductor metal is not disclosed and is not read out of the product name.',
    notes:
      'The upper interconnect in the range, physically the same flat construction. Nothing in the electrical model separates it from the Blue II interconnect except the assumed conductor area, and on a 1 m interconnect conductor area is close to irrelevant: the loop resistance sits in series with a load of tens of kilohms.',
  }),

  makeCable({
    mfr: 'Tellurium Q',
    model: 'Black Power Cable',
    cableType: 'power',
    price: usd(480, 'APPROXIMATE', 2025, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    termination: ['IEC C13', 'BS 1363', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#3a3f4a',
    tags: ['mains-cord', 'undisclosed-construction'],
    provenanceNote: TQ_UNDISCLOSED + ' No current rating, conductor size or shielding specification is published for this cord, so the current rating field is left empty rather than filled with the rating of the plug fuse.',
    notes:
      'A three-core moulded mains cord offered in the usual regional plug variants with an IEC C13 at the component end. Conductor size, shielding and current rating are not published by the maker, so the gauge here is an inference from the cord\'s diameter. The engine applies no signal processing to mains cords, so this entry contributes its price and its place in the chain description and nothing else.',
  }),

  makeCable({
    mfr: 'Tellurium Q',
    model: 'Ultra Black Power Cable',
    cableType: 'power',
    price: usd(1000, 'APPROXIMATE', 2025, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    termination: ['IEC C13', 'IEC C19', 'BS 1363', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#2f333c',
    tags: ['mains-cord', 'undisclosed-construction'],
    provenanceNote: TQ_UNDISCLOSED + ' Gauge is inferred from the cord\'s external diameter and from the C19 option, which implies a conductor set rated for more than 10 A.',
    notes:
      'A heavier mains cord in the same range, additionally offered with an IEC C19 for higher-current inlets. What is verifiable about it is the connector set and the length options. No DSP is applied to power cords by this engine and none is implied here.',
  }),

  makeCable({
    mfr: 'Tellurium Q',
    model: 'Black USB Cable',
    cableType: 'usb',
    price: usd(450, 'APPROXIMATE', 2025, 'Approximate price for a 1 m assembly.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    digitalStandard: 'USB 2.0, Type A to Type B',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#3a3f4a',
    tags: ['usb-2.0', 'undisclosed-construction'],
    provenanceNote: TQ_UNDISCLOSED + ' The Type A to Type B connector pair fixes the standard as USB 2.0; nothing else about the assembly, including whether the 90 ohm differential impedance is held, is published.',
    notes:
      'A Type A to Type B assembly for connecting a computer or server to a DAC. The maker does not publish shielding, conductor size or whether the pair is impedance-controlled, so the construction fields here are inference. USB audio is packetised and error-checked and an asynchronous DAC clocks the data itself, so the engine applies no signal processing to this cable type.',
  }),

  // =========================================================================
  // Black Rhodium -- Derby, United Kingdom
  // =========================================================================
  makeCable({
    mfr: 'Black Rhodium',
    model: 'Twist',
    cableType: 'speaker',
    price: usd(160, 'APPROXIMATE', 2025, 'Approximate cost of a 3 m pair cut from bulk and terminated.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 16,
    termination: ['banana', 'spade', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#8892a6',
    tags: ['twisted', 'bulk-cable', 'entry-range'],
    provenanceNote:
      'Construction type -- a twisted two-conductor speaker cable -- is from the maker\'s own description; the conductor cross-section is inferred from the cable\'s diameter and from its price class, not from a published figure. No electrical data is published for it.',
    notes:
      'The long-running twisted two-core in the range, and the geometry is the whole design: twisting the go and return conductors together shrinks the loop area, which lowers loop inductance relative to a spaced pair and raises capacitance. At an inferred 16 AWG the loop resistance is about 0.026 ohm/m, so a 3 m pair adds around 0.079 ohm -- roughly 0.17 dB into 4 ohms, which is the largest single effect this cable has.',
  }),

  makeCable({
    mfr: 'Black Rhodium',
    model: 'Jive',
    cableType: 'speaker',
    price: usd(280, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 15,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#8892a6',
    tags: ['twisted', 'entry-range'],
    provenanceNote:
      'Twisted construction from the maker\'s description; gauge inferred from cable diameter. Black Rhodium publishes no resistance, inductance or capacitance figures.',
    notes:
      'A twisted pair a gauge up from the Twist. About 0.021 ohm/m of loop resistance gives roughly 0.063 ohm over a 3 m pair, near 0.13 dB into 4 ohms. At 0.6 uH/m the series reactance at 20 kHz over that run is about 0.23 ohm, which into a 4 ohm load is a further couple of hundredths of a decibel at the top of the band.',
  }),

  makeCable({
    mfr: 'Black Rhodium',
    model: 'Samba',
    cableType: 'speaker',
    price: usd(500, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    dielectric: 'Silicone rubber',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#7e879a',
    tags: ['twisted', 'cryogenic-treatment'],
    provenanceNote:
      'Silicone insulation and the maker\'s DCT deep cryogenic process are from published product descriptions; gauge is inferred. No electrical figures are published, and no change to loop R, L or C is published for the cryogenic process either, so none is applied.',
    notes:
      'Twisted pair with silicone insulation, and one of the models the maker puts through its deep cryogenic treatment after assembly. The treatment is a metallurgical process applied to the finished cable; there is no published measurement of it altering loop resistance, inductance or capacitance, so the engine derives those three from gauge and geometry as usual. At 14 AWG a 3 m pair contributes about 0.050 ohm, or 0.11 dB into 4 ohms.',
  }),

  makeCable({
    mfr: 'Black Rhodium',
    model: 'Salsa',
    cableType: 'speaker',
    price: usd(780, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    dielectric: 'Silicone rubber',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#7e879a',
    tags: ['twisted', 'cryogenic-treatment'],
    provenanceNote:
      'Construction class from the maker\'s description; conductor cross-section inferred. Position within the range is not asserted here because the maker\'s pricing varies by market and the ordering could not be confirmed.',
    notes:
      'Another silicone-insulated twisted pair, cryogenically treated, sitting above the Samba. Electrically it is close to indistinguishable from it: same geometry, same nominal conductor area, so the derived loop resistance of roughly 0.017 ohm/m and 0.6 uH/m of inductance are the same numbers. Whatever separates the two in the maker\'s range is not something with a published electrical figure attached.',
  }),

  makeCable({
    mfr: 'Black Rhodium',
    model: 'Tango',
    cableType: 'speaker',
    price: usd(1400, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    dielectric: 'Silicone rubber',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#6f788b',
    tags: ['twisted', 'cryogenic-treatment'],
    provenanceNote:
      'Twisted silicone-insulated construction and cryogenic treatment from published descriptions; gauge inferred from cable diameter. No published R, L or C.',
    notes:
      'A heavier twisted pair. Loop resistance near 0.013 ohm/m puts about 0.039 ohm in series over a 3 m pair, roughly 0.09 dB into a 4 ohm minimum. The step down in resistance from the Salsa is worth about 0.02 dB at that load, which is an honest statement of what a gauge step buys on a normal domestic run.',
  }),

  makeCable({
    mfr: 'Black Rhodium',
    model: 'Charleston',
    cableType: 'speaker',
    price: usd(2200, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Silicone rubber',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#6f788b',
    tags: ['twisted', 'cryogenic-treatment', 'upper-range'],
    provenanceNote:
      'Construction class from the maker\'s description; conductor cross-section and price are both approximations. Black Rhodium does not publish electrical data for any cable in the range.',
    notes:
      'One of the heavier twisted pairs in the range. At an inferred 12 AWG the loop resistance falls to about 0.010 ohm/m, so a 3 m pair adds roughly 0.031 ohm -- around 0.07 dB into 4 ohms, or 0.09 dB into a 3 ohm dip. Twisting keeps the inductance near 0.6 uH/m, which at 20 kHz over 3 m is about 0.23 ohm of reactance.',
  }),

  makeCable({
    mfr: 'Black Rhodium',
    model: 'Foxtrot',
    cableType: 'speaker',
    price: usd(3200, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Silicone rubber',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#5f6779',
    tags: ['twisted', 'cryogenic-treatment', 'upper-range'],
    provenanceNote:
      'Construction class from the maker\'s description. Gauge, price and range position are all approximations; the maker publishes no cross-section and no electrical data.',
    notes:
      'An upper twisted-pair model with the same silicone insulation and cryogenic process as the rest of the line. Its derived figures sit alongside the Charleston because the geometry and the assumed conductor area are the same. This database will not manufacture a difference between two cables when the published facts do not contain one.',
  }),

  makeCable({
    mfr: 'Black Rhodium',
    model: 'Minuet',
    cableType: 'interconnect',
    price: usd(150, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper screen',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#8892a6',
    tags: ['screened', 'entry-range'],
    provenanceNote:
      'A screened single-ended interconnect; the coaxial construction follows from the RCA termination and the screen, and the conductor size is inferred. No electrical figures are published.',
    notes:
      'A conventional screened coaxial RCA lead. Coaxial construction sits near 85 pF/m, so a 1 m pair is about 85 pF per channel; against a 600 ohm source that is a corner above 3 MHz and against a 100 ohm source above 18 MHz. The screen is doing the useful work here, and its benefit is rejection of externally coupled interference rather than anything in the audio band.',
  }),

  makeCable({
    mfr: 'Black Rhodium',
    model: 'Prelude',
    cableType: 'interconnect',
    price: usd(330, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided copper screen',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#7e879a',
    tags: ['screened', 'balanced', 'cryogenic-treatment'],
    provenanceNote:
      'Screened twisted-pair construction and the availability of a balanced version are from published descriptions; conductor size is inferred. No published R, L or C.',
    notes:
      'A screened twisted pair offered single-ended and balanced. Twisting the signal pair inside the screen is the standard remedy for magnetically coupled interference, which the screen alone does not address. Capacitance around 70 pF/m over a 1 m run has no in-band consequence from any normal source impedance, and the engine will report that as close to 0.00 dB.',
  }),

  // =========================================================================
  // Ecosse Cables -- Glasgow, United Kingdom
  // =========================================================================
  makeCable({
    mfr: 'Ecosse Cables',
    model: 'The Conductor 2.5',
    cableType: 'speaker',
    price: usd(400, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    dielectric: 'Polypropylene',
    termination: ['banana', 'spade', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#3f7d54',
    tags: ['twisted', 'polypropylene-dielectric', 'entry-range'],
    provenanceNote:
      'Conductor metal, dielectric and twisted construction are from the maker\'s published description; the exact conductor cross-section is inferred from the model designation and the cable diameter rather than quoted. No electrical figures are published.',
    notes:
      'A twisted two-core with a polypropylene dielectric, which is a low-loss insulator with a dielectric constant near 2.2 -- that is the material fact behind a moderate capacitance figure. At an inferred 14 AWG, loop resistance of about 0.017 ohm/m puts roughly 0.050 ohm across a 3 m pair, or 0.11 dB into 4 ohms.',
  }),

  makeCable({
    mfr: 'Ecosse Cables',
    model: 'The Composer',
    cableType: 'speaker',
    price: usd(1300, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Polypropylene',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#2f6b45',
    tags: ['twisted', 'monocrystal-conductor', 'upper-range'],
    provenanceNote:
      'The maker describes the conductor as its monocrystal copper, which is treated here as ordinary continuous-cast OCC for resistivity purposes -- 1.71e-8 ohm.m against 1.724e-8 for OFC, a difference of under one per cent. Gauge is inferred from diameter; no electrical figures are published.',
    notes:
      'A heavier twisted pair using the maker\'s monocrystal copper. The metallurgy buys under one per cent of resistivity against ordinary OFC, which at 12 AWG over 3 m is a change of well under a milliohm; the gauge step from the Conductor is worth about forty times more. Loop resistance lands near 0.010 ohm/m for about 0.07 dB into 4 ohms over the pair.',
  }),

  makeCable({
    mfr: 'Ecosse Cables',
    model: 'The Diva 2',
    cableType: 'interconnect',
    price: usd(170, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Braided copper screen',
    dielectric: 'Polypropylene',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#3f7d54',
    tags: ['screened', 'entry-range'],
    provenanceNote:
      'Screened single-ended construction and the polypropylene dielectric are from published descriptions; conductor size is inferred. No electrical figures are published.',
    notes:
      'The entry screened RCA lead. Coaxial geometry with a polypropylene dielectric puts capacitance around 85 pF/m; a 1 m pair is therefore about 85 pF per channel, which is inaudible against any normal line output and is computed as such rather than assumed away.',
  }),

  makeCable({
    mfr: 'Ecosse Cables',
    model: 'The Baton MA2',
    cableType: 'interconnect',
    price: usd(240, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper screen',
    dielectric: 'Polypropylene',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#3f7d54',
    tags: ['screened', 'ma2-designation'],
    provenanceNote:
      'MA2 is the maker\'s own designation for a treatment applied to this line; what the process consists of is not published in enough detail to characterise, and no electrical effect is claimed for it here. Gauge inferred; no published R, L or C.',
    notes:
      'A screened coaxial RCA lead in the maker\'s MA2 series. The construction facts that matter electrically are the coaxial geometry, the polypropylene dielectric and the conductor size, and those are what the derived figures come from. The MA2 designation is recorded as a product fact and is given no modelled consequence.',
  }),

  makeCable({
    mfr: 'Ecosse Cables',
    model: 'MA2 Maestro',
    cableType: 'interconnect',
    price: usd(430, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper screen',
    dielectric: 'Polypropylene',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#4f9166',
    tags: ['screened', 'silver-plated', 'ma2-designation'],
    provenanceNote:
      'Silver-plated copper conductors are from the maker\'s description. Plating is a surface treatment: the DC path is still the copper core, so the kit uses copper resistivity for it rather than silver, which is the physically correct choice. Gauge inferred.',
    notes:
      'Silver-plated copper in a screened coaxial construction. Plating matters at radio frequencies where skin depth confines current to the outer few microns; at 20 kHz the skin depth in copper is about 0.47 mm, which is wider than the whole conductor here, so the current uses the copper core and the plating contributes essentially nothing to the audio-band resistance.',
  }),

  makeCable({
    mfr: 'Ecosse Cables',
    model: 'The Director MA2',
    cableType: 'interconnect',
    price: usd(700, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil plus braided screen',
    dielectric: 'Polypropylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#2f6b45',
    tags: ['screened', 'balanced', 'monocrystal-conductor'],
    provenanceNote:
      'Monocrystal copper conductors and the balanced option are from published descriptions; the twisted-pair geometry follows from the balanced construction. Conductor size inferred, no published electrical data.',
    notes:
      'A twin-conductor screened cable offered terminated either single-ended or balanced. The twisted pair inside a foil-and-braid screen is the textbook arrangement: the screen handles electrostatic pickup, the twist handles magnetic pickup. Capacitance near 70 pF/m over 1 m is of no in-band consequence at any realistic source impedance.',
  }),

  makeCable({
    mfr: 'Ecosse Cables',
    model: 'Big Red',
    cableType: 'power',
    price: usd(300, 'APPROXIMATE', 2025, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 13,
    termination: ['IEC C13', 'BS 1363', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#8c3a34',
    tags: ['mains-cord', 'high-current'],
    provenanceNote:
      'Three-core mains construction is from the product description; the conductor cross-section is inferred from the cord diameter, and the 13 A figure is the rating implied by the UK plug and fuse it is normally supplied with rather than a separately published cable rating.',
    notes:
      'A three-core mains cord terminated with a moulded IEC C13 and a regional wall plug. The conductor set is sized to carry the full current of a UK 13 A outlet without meaningful voltage drop over the cord length. The engine applies no signal processing to mains cords, so this entry contributes cost and chain description only.',
  }),

  // =========================================================================
  // Townshend Audio -- Isleworth, United Kingdom
  // =========================================================================
  makeCable({
    mfr: 'Townshend Audio',
    model: 'Isolda DCT Speaker Cable',
    cableType: 'speaker',
    price: usd(2000, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'ribbon',
    gauge: 14,
    dielectric: 'Thin polymer film',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4622a',
    tags: ['ribbon', 'low-inductance', 'high-capacitance', 'cryogenic-treatment'],
    provenanceNote:
      'The flat, wide, closely spaced conductor arrangement is the documented core of this design and the maker has always described it in those terms. The specific per-metre inductance and capacitance figures are derived from that geometry by the kit, not quoted from the maker.',
    notes:
      'A flat ribbon: two wide conductors separated by a thin film, which is the geometry that minimises loop area. That drives inductance down towards 0.05 uH/m -- about a twelfth of an ordinary twisted pair, or roughly 0.02 ohm of reactance at 20 kHz over a 3 m run -- and drives capacitance up in the same proportion, to the order of 600 pF/m or nearly 2 nF across a 3 m pair. That capacitance is a real load on the amplifier: makers of flat high-capacitance designs commonly supply or recommend a Zobel network at the amplifier end, because an amplifier that is only marginally stable can misbehave into a load like this.',
  }),

  makeCable({
    mfr: 'Townshend Audio',
    model: 'Isolda EDCT Speaker Cable',
    cableType: 'speaker',
    price: usd(3200, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'ribbon',
    gauge: 14,
    dielectric: 'Thin polymer film',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c2551f',
    tags: ['ribbon', 'low-inductance', 'high-capacitance', 'cryogenic-treatment'],
    provenanceNote:
      'Same documented ribbon geometry as the DCT version, with the maker\'s extended cryogenic process applied. No measurement of that process changing loop R, L or C is published, so the derived figures are identical to the DCT version and the difference between the two entries is honestly nothing the engine can act on.',
    notes:
      'The Isolda with the maker\'s extended deep cryogenic treatment. Electrically it is the same cable: same ribbon geometry, same conductor area, so the same very low inductance and very high capacitance. Anyone comparing this entry with the DCT version in the simulator will find identical processing, which is the correct outcome given what is actually published about the difference.',
  }),

  makeCable({
    mfr: 'Townshend Audio',
    model: 'F1 Fractal Speaker Cable',
    cableType: 'speaker',
    price: usd(1500, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 14,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#d4622a',
    tags: ['flat-jacket', 'low-inductance', 'cryogenic-treatment'],
    provenanceNote:
      'The F1 Fractal line continues the maker\'s flat, closely spaced construction, which is described in the product literature; the exact conductor arrangement and cross-section are not quoted, so flat-parallel and 14 AWG are inferences rather than statements.',
    notes:
      'A flat, closely spaced pair. The geometry lands between an outright ribbon and a twisted pair: around 0.12 uH/m of inductance and 300 pF/m of capacitance. Over a 3 m run that is about 0.045 ohm of reactance at 20 kHz and 900 pF of load, with roughly 0.050 ohm of loop resistance doing most of the audible work at 0.11 dB into 4 ohms.',
  }),

  makeCable({
    mfr: 'Townshend Audio',
    model: 'F1 Fractal Interconnect',
    cableType: 'interconnect',
    price: usd(900, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Braided screen',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d4622a',
    tags: ['screened', 'cryogenic-treatment'],
    provenanceNote:
      'Screened construction and the RCA and XLR options are from the product listing; conductor size and internal geometry are inferred. No electrical figures are published for this cable.',
    notes:
      'A screened interconnect offered single-ended and balanced. Modelled as a coaxial construction at about 85 pF/m, a 1 m pair carries roughly 85 pF per channel; against a 2.2 kohm valve preamplifier output that is a corner near 850 kHz, which is the highest-impedance realistic case and still five octaves clear of the audio band.',
  }),

  makeCable({
    mfr: 'Townshend Audio',
    model: 'F1 Fractal Mains Cable',
    cableType: 'power',
    price: usd(700, 'APPROXIMATE', 2025, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 13,
    termination: ['IEC C13', 'BS 1363', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#b34d1c',
    tags: ['mains-cord', 'cryogenic-treatment'],
    provenanceNote:
      'Three-core mains construction and the cryogenic treatment are from the product listing; conductor cross-section is inferred from cord diameter, and the current rating is that of the UK plug and fuse rather than a separately published cable figure.',
    notes:
      'A three-core mains lead with the maker\'s cryogenic treatment, terminated in a moulded IEC C13 and a regional wall plug. Its rating is adequate for the current a domestic amplifier draws, which is the specification that matters for a mains cord. No signal processing is applied to power cords by this engine.',
  }),

  // =========================================================================
  // Furutech -- Tokyo, Japan
  // =========================================================================
  makeCable({
    mfr: 'Furutech',
    model: 'FP-TCS31',
    cableType: 'power',
    price: usd(420, 'APPROXIMATE', 2025, 'Approximate cost of a 1.5 m cord assembled from bulk cable with the maker\'s mid-range connectors.'),
    priceLengthM: 1.5,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Copper foil with a drain',
    termination: ['IEC C13', 'NEMA 5-15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b3202e',
    tags: ['bulk-cable', 'mains-cord', 'shielded'],
    provenanceNote:
      'Sold as bulk cable, so the price is for a cord assembled from it rather than a finished product price. Conductor metal and approximate cross-section are published; the AWG figure here is the nearest gauge to the quoted square-millimetre section.',
    notes:
      'Three conductors of the maker\'s alpha-process continuous-cast copper at roughly 3.3 sq mm each, which is close to 12 AWG, twisted together inside a foil screen. It is the bulk cable most of the maker\'s own cords and dealer-assembled leads are built from, and it is rated for 15 A service. The engine applies no signal processing to mains cords.',
  }),

  makeCable({
    mfr: 'Furutech',
    model: 'The Astoria',
    cableType: 'power',
    price: usd(1700, 'APPROXIMATE', 2025, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Copper foil and braid',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#b3202e',
    tags: ['mains-cord', 'shielded', 'ncf-connectors'],
    provenanceNote:
      'Conductor metal and connector family are from the product listing; the conductor cross-section is inferred from the cord diameter rather than quoted, so this entry is marked estimated rather than manufacturer spec.',
    notes:
      'A finished mains cord using the maker\'s alpha-process copper with rhodium-plated connectors from its own NCF connector line. NCF is a piezo-ceramic and carbon filler in the connector body: it is a mechanical property of the housing and has no bearing on loop resistance, inductance or capacitance, so it is recorded here as a construction fact with no modelled consequence. No DSP is applied to power cords.',
  }),

  makeCable({
    mfr: 'Furutech',
    model: 'Powerflux NCF',
    cableType: 'power',
    price: usd(2400, 'APPROXIMATE', 2025, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    shielding: 'Multi-layer foil and braid',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#9c1b27',
    tags: ['mains-cord', 'shielded', 'ncf-connectors', 'high-current'],
    provenanceNote:
      'Conductor metal, multi-layer shielding and NCF connector bodies are published; the conductor cross-section is inferred from the cord\'s external diameter and its C19 option.',
    notes:
      'A heavy multi-layer shielded cord with the maker\'s NCF connectors at both ends, offered with a C19 for inlets drawing more than 10 A. At an inferred 11 AWG the conductor set is generously sized for any domestic amplifier. Everything above is construction: the engine applies no signal processing to this cable type, and a cord at this price is no exception.',
  }),

  makeCable({
    mfr: 'Furutech',
    model: 'Nanoflux Power NCF',
    cableType: 'power',
    price: usd(6000, 'APPROXIMATE', 2025, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    shielding: 'Multi-layer foil and braid',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#9c1b27',
    tags: ['mains-cord', 'shielded', 'ncf-connectors', 'upper-range'],
    provenanceNote:
      'The nano-particle liquid the range is named for is applied at conductor and contact interfaces; the maker publishes no conductivity or resistance figure for it, so it is recorded as a construction fact and given no derived consequence. Cross-section inferred.',
    notes:
      'The Nanoflux mains cord: alpha-process copper, multi-layer shielding, NCF connector bodies and the nano-particle treatment the line is named for. This is an elaborately built piece of cable engineering with no published electrical figures attached to any of its distinguishing features, and it sits upstream of the rectifier and regulator of whatever it feeds. The engine applies exactly zero signal processing to it.',
  }),

  makeCable({
    mfr: 'Furutech',
    model: 'The Empire',
    cableType: 'power',
    price: usd(11000, 'APPROXIMATE', 2025, 'Approximate price for a 1.8 m cord; dealer pricing varies widely.'),
    priceLengthM: 1.8,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Multi-layer foil and braid over a damped core',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#7d151f',
    tags: ['mains-cord', 'shielded', 'ncf-connectors', 'top-of-range'],
    provenanceNote:
      'Flagship mains cord; conductor metal and connector family are published, cross-section is inferred from the cord\'s diameter and mass. Price is approximate and varies by region and connector choice.',
    notes:
      'The largest mains cord in the range: a very heavy alpha-process copper conductor set, layered shielding, mechanical damping in the jacket and NCF-bodied rhodium-plated connectors. It is a genuinely serious piece of manufacturing, and the honest statement about it in this simulation is that a mains cord sits ahead of the rectifier, reservoir and regulation of the component it feeds and the engine applies no processing to it at all. Selecting it changes the chain description and the total price and leaves the audio bit-identical.',
  }),

  makeCable({
    mfr: 'Furutech',
    model: 'Lineflux NCF',
    cableType: 'interconnect',
    price: usd(3000, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 20,
    shielding: 'Foil and braid with a separate drain',
    dielectric: 'Foamed polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#b3202e',
    tags: ['screened', 'balanced', 'ncf-connectors', 'upper-range'],
    provenanceNote:
      'Conductor metal, foamed dielectric and NCF connector bodies are from the published description; conductor cross-section is inferred. No electrical figures are published.',
    notes:
      'A heavy screened interconnect with a foamed polyethylene dielectric -- foaming lowers the effective dielectric constant towards 1.5, which is the material reason a foamed cable has lower capacitance than a solid-dielectric one of the same dimensions. As a twisted pair around 70 pF/m, a 1 m link is about 70 pF and the derived in-band loss from any normal source impedance is a rounding error.',
  }),

  makeCable({
    mfr: 'Furutech',
    model: 'Evolution II Audio',
    cableType: 'interconnect',
    price: usd(700, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Foil and braid',
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#c33a45',
    tags: ['screened', 'entry-range'],
    provenanceNote:
      'Conductor metal and screened construction are published; conductor size is inferred from the cable diameter. No published resistance, inductance or capacitance.',
    notes:
      'A screened coaxial RCA lead in alpha-process copper, the mid-range single-ended interconnect in the line. Coaxial geometry around 85 pF/m puts a 1 m pair near 85 pF per channel. The loop resistance of a 22 AWG interconnect is about 0.11 ohm/m, which against an input impedance of tens of kilohms is a divider of roughly one part in a hundred thousand.',
  }),

  makeCable({
    mfr: 'Furutech',
    model: 'Speakerflux',
    cableType: 'speaker',
    price: usd(4500, 'APPROXIMATE', 2025, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    dielectric: 'Foamed polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#b3202e',
    tags: ['twisted', 'upper-range'],
    provenanceNote:
      'Conductor metal and foamed dielectric are published; the conductor cross-section is inferred from the cable\'s external diameter rather than quoted per conductor.',
    notes:
      'A heavy twisted construction in alpha-process copper with rhodium-plated terminations. At an inferred 11 AWG the loop resistance is about 0.008 ohm/m, so a 2.5 m pair adds roughly 0.021 ohm -- about 0.045 dB into 4 ohms, which is at the edge of what a careful level-matched comparison could resolve. Twisting keeps inductance near 0.6 uH/m: about 0.19 ohm of reactance at 20 kHz over that length.',
  }),

  makeCable({
    mfr: 'Furutech',
    model: 'Evolution II Speaker',
    cableType: 'speaker',
    price: usd(1700, 'APPROXIMATE', 2025, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#c33a45',
    tags: ['twisted'],
    provenanceNote:
      'Conductor metal and construction class are published; cross-section inferred from cable diameter. No published electrical figures.',
    notes:
      'The mid speaker cable in the line, twisted alpha-process copper at an inferred 12 AWG. Loop resistance near 0.010 ohm/m gives about 0.026 ohm over a 2.5 m pair, roughly 0.06 dB into 4 ohms. The step up to the Speakerflux is worth about 0.01 dB at the same load and length, which is the honest size of it.',
  }),

  makeCable({
    mfr: 'Furutech',
    model: 'FS-Alpha 36',
    cableType: 'speaker',
    price: usd(300, 'APPROXIMATE', 2025, 'Approximate cost of a 3 m pair cut from bulk cable, unterminated.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['bare', 'spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c33a45',
    tags: ['bulk-cable', 'twisted'],
    provenanceNote:
      'Sold by the metre as bulk cable, so the quoted price is for a self-terminated 3 m pair. The 3.6 sq mm conductor section in the model name is a published figure; 12 AWG is the nearest standard gauge to it.',
    notes:
      'Bulk two-core speaker cable with about 3.6 sq mm per conductor, which is just heavier than 12 AWG, in the maker\'s alpha-process copper. Loop resistance works out near 0.010 ohm/m, so a 3 m pair contributes roughly 0.031 ohm and about 0.07 dB into 4 ohms. Electrically it is the equal of cables in this file costing fifteen times as much, because conductor area and geometry are what the physics depends on.',
  }),

  makeCable({
    mfr: 'Furutech',
    model: 'GT2 Pro',
    cableType: 'usb',
    price: usd(180, 'APPROXIMATE', 2025, 'Approximate price for a 1.2 m assembly.'),
    priceLengthM: 1.2,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid, with separated power and data pairs',
    termination: ['USB A to B', 'USB A to Micro-B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b3202e',
    tags: ['usb-2.0', 'shielded'],
    provenanceNote:
      'Conductor metal, the separation of the power and signal pairs and the connector options are published. The 90 ohm differential impedance is the USB 2.0 requirement the assembly is built to meet rather than a separately measured figure.',
    notes:
      'A USB 2.0 assembly in alpha-process copper with the bus power pair run separately from the data pair inside the screen. Built to the 90 ohm differential impedance the standard requires and terminated in gold-plated connectors. USB audio is packetised and error-checked and an asynchronous DAC generates its own clock, so the engine applies no signal processing here: a compliant cable carries the data or it fails.',
  }),

  makeCable({
    mfr: 'Furutech',
    model: 'LAN-8 NCF',
    cableType: 'ethernet',
    price: usd(400, 'APPROXIMATE', 2025, 'Approximate price for a 1.2 m patch lead.'),
    priceLengthM: 1.2,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'star-quad',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Cat 8',
    shielding: 'S/FTP, individually foiled pairs inside an overall braid',
    termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b3202e',
    tags: ['cat8', 'shielded', 'ncf-connectors'],
    provenanceNote:
      'Cat 8 category, the S/FTP shielding scheme and the NCF-treated RJ45 housings are published. The 100 ohm figure is the impedance the category mandates.',
    notes:
      'A Cat 8 patch lead: four individually foiled pairs inside an overall braid, 100 ohm differential impedance, in NCF-treated RJ45 housings. Ethernet is packetised, checksummed and retransmitted on error, and a streamer buffers seconds of audio ahead of the converter, so there is no analog path between the two ends. The engine applies no signal processing to this cable type.',
  }),

  // =========================================================================
  // Oyaide -- Tokyo, Japan
  // =========================================================================
  makeCable({
    mfr: 'Oyaide',
    model: 'Tunami GPX-R V2',
    cableType: 'power',
    price: usd(500, 'APPROXIMATE', 2025, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Carbon-loaded semiconducting layer with a braided screen',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2b6cb0',
    tags: ['mains-cord', 'shielded', 'high-current'],
    provenanceNote:
      'Conductor metal and the 3.5 sq mm per-conductor section are published by the maker; 12 AWG is the nearest standard gauge to that section. The maker\'s 102 SSC copper is a high-purity annealed copper and is treated as OFC for resistivity.',
    notes:
      'Three conductors of about 3.5 sq mm -- near 12 AWG -- in the maker\'s 102 SSC copper, twisted, with a carbon-loaded semiconducting layer under a braided screen and terminated in the maker\'s own moulded plugs. Rated for 15 A. The engine applies no signal processing to mains cords; this entry is here for its construction and its price.',
  }),

  makeCable({
    mfr: 'Oyaide',
    model: 'Tunami Terzo GPX-R',
    cableType: 'power',
    price: usd(800, 'APPROXIMATE', 2025, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Carbon-loaded semiconducting layer with a braided screen',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#2b6cb0',
    tags: ['mains-cord', 'shielded'],
    provenanceNote:
      'Same conductor family as the Tunami GPX-R with a revised internal arrangement; the maker publishes the conductor metal but the per-conductor section for this variant is inferred from the base cable.',
    notes:
      'A development of the Tunami cord with a reworked internal structure and the same 102 SSC copper conductor set. Construction, connector set and current rating are what is real about it. No DSP is applied to power cords.',
  }),

  makeCable({
    mfr: 'Oyaide',
    model: 'Black Mamba-Sigma V2',
    cableType: 'power',
    price: usd(380, 'APPROXIMATE', 2025, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 15,
    shielding: 'Braided copper screen',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#3a4a5c',
    tags: ['mains-cord', 'shielded'],
    provenanceNote:
      'The maker writes this model name with a Greek sigma; it is transliterated here because this database is ASCII-only, so the string will not match the maker\'s own spelling character for character. Conductor cross-section is inferred from the cord diameter.',
    notes:
      'A three-core screened mains cord below the Tunami in the range, at an inferred 13 AWG. Construction and connectors are the substance of it. Power cords receive no signal processing in this engine.',
  }),

  makeCable({
    mfr: 'Oyaide',
    model: 'L/i 50 EXs',
    cableType: 'power',
    price: usd(300, 'APPROXIMATE', 2025, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 12,
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#3f7fb8',
    tags: ['mains-cord', 'entry-range'],
    provenanceNote:
      'Conductor metal is published; the cross-section is inferred from the cord\'s diameter. The current rating shown is the conventional rating for a cord of that section rather than a separately published figure.',
    notes:
      'The compact mains cord in the line, a three-core at an inferred 14 AWG with the maker\'s moulded connectors. Adequate for source components and integrated amplifiers of ordinary draw. As with every mains cord in this database, the engine applies no processing to the audio when it is selected.',
  }),

  makeCable({
    mfr: 'Oyaide',
    model: 'EE/F-S 2.6 V2',
    cableType: 'power',
    price: usd(200, 'APPROXIMATE', 2025, 'Approximate cost of a 1.8 m cord assembled from bulk cable with mid-range connectors.'),
    priceLengthM: 1.8,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f7fb8',
    tags: ['bulk-cable', 'mains-cord'],
    provenanceNote:
      'Sold as bulk cable by the metre, so the price is for a self-assembled cord. The 2.6 sq mm conductor section is published in the model designation; 13 AWG is the nearest standard gauge.',
    notes:
      'Bulk three-core mains cable with 2.6 sq mm conductors, which is a shade over 13 AWG, in the maker\'s 102 SSC copper. It is the cable a great many dealer-assembled and DIY cords are built from, and it meets Japanese domestic wiring practice for fixed and flexible use. No signal processing is applied to power cords.',
  }),

  makeCable({
    mfr: 'Oyaide',
    model: 'Across 750 RR V2',
    cableType: 'interconnect',
    price: usd(300, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper screen with a carbon-loaded layer',
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2b6cb0',
    tags: ['screened', 'balanced-construction'],
    provenanceNote:
      'Conductor metal, the carbon-loaded semiconducting layer and the screened construction are published; the conductor cross-section is inferred from the cable diameter.',
    notes:
      'A screened RCA interconnect in 102 SSC copper with a carbon-loaded semiconducting layer between conductor and screen -- a construction borrowed from power cable practice, where that layer suppresses charge accumulation at the dielectric boundary. Coaxial geometry near 85 pF/m means a 1 m pair is about 85 pF per channel, which computes to essentially nothing in the audio band.',
  }),

  makeCable({
    mfr: 'Oyaide',
    model: 'PA-02 TX V2',
    cableType: 'interconnect',
    price: usd(130, 'APPROXIMATE', 2025, 'Approximate price for a 1 m balanced pair made from bulk cable.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Spiral copper shield with a drain wire',
    dielectric: 'Polyethylene',
    termination: ['XLR', 'TRS'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f7fb8',
    tags: ['pro-audio', 'balanced', 'bulk-cable'],
    provenanceNote:
      'A professional balanced cable sold in bulk; conductor metal, the twisted pair with drain and the shield type are published. The exact conductor section is inferred, and no per-metre capacitance figure is carried over from the maker\'s datasheet because it could not be recalled with confidence.',
    notes:
      'The maker\'s professional balanced cable: a twisted pair with a drain wire inside a spiral shield, made for XLR and TRS work. Flexible enough to coil and cheap enough to buy by the reel, and electrically it is the same class of object as balanced interconnects here costing twenty times more -- a twisted pair around 70 pF/m, which over 1 m is inconsequential from any professional or domestic output stage.',
  }),

  makeCable({
    mfr: 'Oyaide',
    model: 'FTVS-510',
    cableType: 'interconnect',
    price: usd(500, 'APPROXIMATE', 2025, 'Approximate cost of a 1 m pair built from bulk wire.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'pure-silver',
    geometry: 'solid-core',
    gauge: 20,
    dielectric: 'PTFE',
    termination: ['RCA', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9fb0bf',
    tags: ['solid-core', 'ptfe-dielectric', 'bulk-cable'],
    provenanceNote:
      'Solid silver conductor with PTFE insulation, sold as bulk wire; the 0.5 sq mm section is published and corresponds closely to 20 AWG. Price is for a self-built pair, since the wire is sold by the metre.',
    notes:
      'Solid silver conductor of about 0.5 sq mm in PTFE, sold by the metre for internal wiring and self-built interconnects. Silver\'s resistivity of 1.587e-8 ohm.m is roughly eight per cent below copper, which on a 1 m interconnect feeding a 47 kohm input is a difference of a few milliohms against a load of tens of thousands of ohms. The solid-core geometry with a wide conductor spacing is the low-capacitance, high-inductance end of the trade-off, near 45 pF/m.',
  }),

  makeCable({
    mfr: 'Oyaide',
    model: 'Explorer v2',
    cableType: 'speaker',
    price: usd(500, 'APPROXIMATE', 2025, 'Approximate price for a 3 m pair cut from bulk cable.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    dielectric: 'Polyethylene',
    termination: ['bare', 'banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#2b6cb0',
    tags: ['bulk-cable', 'twisted'],
    provenanceNote:
      'Conductor metal is published; the conductor section is inferred from the cable\'s external diameter. Sold by the metre, so the price is for a self-terminated pair.',
    notes:
      'A twisted two-core speaker cable in 102 SSC copper sold by the metre. At an inferred 13 AWG the loop resistance is about 0.013 ohm/m, so a 3 m pair adds roughly 0.039 ohm -- about 0.09 dB into a 4 ohm minimum, which is the dominant modelled effect of any speaker cable of ordinary length.',
  }),

  makeCable({
    mfr: 'Oyaide',
    model: 'OR-800 Advance',
    cableType: 'speaker',
    price: usd(900, 'APPROXIMATE', 2025, 'Approximate price for a 3 m pair cut from bulk cable.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['bare', 'banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#1f5c9e',
    tags: ['bulk-cable', 'twisted', 'upper-range'],
    provenanceNote:
      'Conductor metal published; conductor section inferred from cable diameter. Sold by the metre; the price shown is for a self-terminated 3 m pair.',
    notes:
      'A heavier twisted speaker cable in the same 102 SSC copper. Loop resistance near 0.010 ohm/m puts about 0.031 ohm across a 3 m pair, roughly 0.07 dB into 4 ohms. Against the Explorer v2 that is a difference of about two hundredths of a decibel at the same length and load.',
  }),

  makeCable({
    mfr: 'Oyaide',
    model: 'Continental 5S',
    cableType: 'usb',
    price: usd(350, 'APPROXIMATE', 2025, 'Approximate price for a 1.2 m assembly.'),
    priceLengthM: 1.2,
    country: 'Japan',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid with the power pair separated',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2b6cb0',
    tags: ['usb-2.0', 'shielded', 'silver-plated'],
    provenanceNote:
      'Silver-plated conductors, the separated bus-power pair and the shielding scheme are published. The 90 ohm figure is the differential impedance the USB 2.0 standard requires of a compliant assembly.',
    notes:
      'A USB 2.0 A-to-B assembly with silver-plated conductors, the bus power pair routed away from the data pair, and foil-plus-braid shielding. Built to the 90 ohm differential impedance the standard specifies. The engine applies no signal processing to USB cables: the transfer is packetised and error-checked, and the converter\'s own clock, not the cable, determines timing in an asynchronous DAC.',
  }),

  makeCable({
    mfr: 'Oyaide',
    model: 'd+ USB Class B rev.2',
    cableType: 'usb',
    price: usd(45, 'CURRENT_RETAIL', 2025, 'Retail price for a 1 m assembly.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f7fb8',
    tags: ['usb-2.0', 'shielded', 'entry-range'],
    provenanceNote:
      'Conductor metal, shielding and the USB 2.0 connector pair are published. Impedance is the standard\'s requirement rather than a measured figure for this sample.',
    notes:
      'The inexpensive USB 2.0 assembly in the maker\'s d+ range, foil-and-braid shielded with moulded A and B connectors. It meets the same USB 2.0 requirements as the Continental above it at a fifteenth of the price, and since the engine applies no processing to USB cables, the two produce identical audio in this simulation.',
  }),

  // =========================================================================
  // Acrolink -- Japan
  // =========================================================================
  makeCable({
    mfr: 'Acrolink',
    model: '7N-PC4030 II',
    cableType: 'power',
    price: usd(900, 'APPROXIMATE', 2025, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Copper braid',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#9a6b3f',
    tags: ['mains-cord', 'shielded', 'high-purity-copper'],
    provenanceNote:
      'The maker publishes the 7N-class continuous-cast copper conductor; the conductor cross-section is inferred from the model designation and the cord diameter rather than read from a datasheet, so this entry is marked estimated.',
    notes:
      'A three-core mains cord in the maker\'s 7N-class continuous-cast copper under a braided screen. Very high purity copper is a real metallurgical distinction, and its effect on bulk resistivity against ordinary OFC is under one per cent -- which on a mains cord is irrelevant twice over, since the engine applies no signal processing to power cords at all.',
  }),

  makeCable({
    mfr: 'Acrolink',
    model: '7N-PC6500',
    cableType: 'power',
    price: usd(1900, 'APPROXIMATE', 2025, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    shielding: 'Copper braid over a semiconducting layer',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#9a6b3f',
    tags: ['mains-cord', 'shielded', 'high-current'],
    provenanceNote:
      'Conductor metal and shielding scheme are published; cross-section inferred from the cord\'s diameter and the availability of a C19 termination.',
    notes:
      'A heavier three-core cord with a semiconducting layer under the braid, offered with a C19 for higher-current inlets. At an inferred 11 AWG the conductor set will carry a large amplifier\'s draw with a voltage drop of a few tens of millivolts over 1.5 m. Construction only: no DSP is applied to power cords.',
  }),

  makeCable({
    mfr: 'Acrolink',
    model: '7N-PC9500 Mexcel',
    cableType: 'power',
    price: usd(4800, 'APPROXIMATE', 2025, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Copper braid over a semiconducting layer',
    dielectric: 'Mexcel treated insulation',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#8a5a2f',
    tags: ['mains-cord', 'shielded', 'high-purity-copper', 'upper-range'],
    provenanceNote:
      'The Mexcel designation refers to an insulation and treatment process from the parent wire company. The maker publishes that it is applied but no dielectric constant or loss figure for it, so it is recorded as a construction fact with no derived consequence. Cross-section inferred.',
    notes:
      'A very heavy three-core cord using the parent company\'s Mexcel-treated insulation over 7N-class continuous-cast copper. At an inferred 10 AWG the conductor set is larger than most domestic mains wiring behind the wall it plugs into. This is a well-made cord and the engine applies zero signal processing to it, which is the correct treatment of a component that sits ahead of a rectifier.',
  }),

  makeCable({
    mfr: 'Acrolink',
    model: '7N-PC9700 Mexcel',
    cableType: 'power',
    price: usd(7500, 'APPROXIMATE', 2025, 'Approximate price for a 1.5 m cord; dealer pricing varies.'),
    priceLengthM: 1.5,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Multi-layer braid over a semiconducting layer',
    dielectric: 'Mexcel treated insulation',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#7a4c26',
    tags: ['mains-cord', 'shielded', 'top-of-range'],
    provenanceNote:
      'Flagship mains cord in the range. Conductor metal and the Mexcel treatment are published; the cross-section is inferred and the price is an approximation across dealers and regions.',
    notes:
      'The largest mains cord in the line: multi-layer shielding, Mexcel-treated insulation, 7N-class copper and a conductor set at an inferred 10 AWG. Everything stated here is construction, because construction is all a mains cord has that this engine can record. Selecting it changes the chain description and the price and nothing in the audio.',
  }),

  makeCable({
    mfr: 'Acrolink',
    model: '6N-A2200 II Mexcel',
    cableType: 'interconnect',
    price: usd(900, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Copper braid',
    dielectric: 'Mexcel treated insulation',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#9a6b3f',
    tags: ['screened', 'high-purity-copper'],
    provenanceNote:
      'Conductor metal, the Mexcel insulation treatment and the screened single-ended construction are published; conductor size is inferred. No electrical figures are published.',
    notes:
      'A screened single-ended interconnect in 6N-class continuous-cast copper with Mexcel-treated insulation. Modelled as a coaxial construction at about 85 pF/m, a 1 m pair is roughly 85 pF per channel; the resulting corner frequency against any normal line output sits in the megahertz, so the computed in-band response is flat to well under a hundredth of a decibel.',
  }),

  makeCable({
    mfr: 'Acrolink',
    model: '7N-A2500',
    cableType: 'interconnect',
    price: usd(1600, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Copper braid with a drain',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#9a6b3f',
    tags: ['screened', 'balanced', 'high-purity-copper'],
    provenanceNote:
      'Conductor metal and the balanced option are published; the internal geometry and conductor size are inferred from the construction a balanced termination requires.',
    notes:
      'A screened interconnect in 7N-class copper, offered terminated for RCA or XLR. As a twisted pair inside a braid it sits near 70 pF/m, so a 1 m link presents about 70 pF. The difference between 6N and 7N purity is a bulk resistivity change well under a per cent, on a conductor whose resistance is already five orders of magnitude below the load it drives.',
  }),

  makeCable({
    mfr: 'Acrolink',
    model: '7N-A2070 Leggenda',
    cableType: 'interconnect',
    price: usd(6000, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 20,
    shielding: 'Multi-layer braid with a drain',
    dielectric: 'Mexcel treated insulation',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8a5a2f',
    tags: ['screened', 'balanced', 'top-of-range'],
    provenanceNote:
      'The flagship interconnect in the range. Conductor metal, Mexcel insulation and multi-layer shielding are published; conductor size and geometry are inferred. No electrical figures are published for it.',
    notes:
      'The top interconnect in the line: heavy 7N-class conductors, Mexcel-treated insulation and multi-layer shielding, terminated single-ended or balanced. The heavier conductor drops loop resistance to about 0.066 ohm/m, which on a 1 m run into a 47 kohm input is a divider of roughly one part in seven hundred thousand. What this cable is buying is mechanical and metallurgical, not a change to the numbers the engine reads.',
  }),

  makeCable({
    mfr: 'Acrolink',
    model: '6N-S1010 III',
    cableType: 'speaker',
    price: usd(700, 'APPROXIMATE', 2025, 'Approximate price for a 3 m pair.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 14,
    dielectric: 'Polyethylene',
    termination: ['bare', 'banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#9a6b3f',
    tags: ['twisted', 'high-purity-copper'],
    provenanceNote:
      'Conductor metal is published; the conductor section is inferred from the cable\'s diameter. Sold by the metre in some markets, so the price is approximate for a 3 m pair.',
    notes:
      'A twisted two-core speaker cable in 6N-class continuous-cast copper. At an inferred 14 AWG the loop resistance is about 0.016 ohm/m, so a 3 m pair contributes roughly 0.049 ohm and about 0.11 dB into a 4 ohm minimum. Continuous-cast copper differs from OFC by less than one per cent in resistivity, so it is the gauge, not the metallurgy, that sets that figure.',
  }),

  makeCable({
    mfr: 'Acrolink',
    model: '7N-S8000',
    cableType: 'speaker',
    price: usd(2600, 'APPROXIMATE', 2025, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#8a5a2f',
    tags: ['twisted', 'high-purity-copper', 'upper-range'],
    provenanceNote:
      'Conductor metal is published; conductor cross-section inferred from cable diameter. No published electrical data.',
    notes:
      'A heavier twisted speaker cable in 7N-class copper. Loop resistance near 0.010 ohm/m gives about 0.031 ohm over a 3 m pair, roughly 0.07 dB into 4 ohms and 0.09 dB into a 3 ohm dip. Inductance stays near 0.6 uH/m for the twisted geometry: about 0.23 ohm of reactance at 20 kHz over that run.',
  }),

  // =========================================================================
  // Zonotone -- Japan
  // =========================================================================
  makeCable({
    mfr: 'Zonotone',
    model: '6NSP-1100a',
    cableType: 'speaker',
    price: usd(110, 'APPROXIMATE', 2025, 'Approximate cost of a 3 m pair cut from bulk cable.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 16,
    dielectric: 'Polyethylene',
    termination: ['bare', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#4f8a5b',
    tags: ['bulk-cable', 'twisted', 'entry-range'],
    provenanceNote:
      'Conductor metal is published; the conductor section is inferred from the cable diameter, since the maker\'s speaker cable model numbers do not encode a cross-section the way its power cable numbers do. Sold by the metre.',
    notes:
      'The entry twisted two-core in the range, sold by the metre. At an inferred 16 AWG the loop resistance is about 0.026 ohm/m, so a 3 m pair adds roughly 0.079 ohm -- about 0.17 dB into 4 ohms, which is the largest single number any cable in this file produces and a good argument for buying a heavier gauge before buying a more expensive brand.',
  }),

  makeCable({
    mfr: 'Zonotone',
    model: '6NSP-2200a Meister',
    cableType: 'speaker',
    price: usd(260, 'APPROXIMATE', 2025, 'Approximate cost of a 3 m pair cut from bulk cable.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 14,
    dielectric: 'Polyethylene',
    termination: ['bare', 'banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4f8a5b',
    tags: ['bulk-cable', 'twisted', 'multi-metal-conductor'],
    provenanceNote:
      'The maker publishes that this cable combines several conductor metals in one bundle -- high-purity copper with silver-plated strands among them -- which is the reason for the hybrid material classification here. The overall cross-section is inferred from the cable diameter.',
    notes:
      'A twisted two-core whose conductor bundle deliberately mixes metals: high-purity copper strands alongside silver-plated ones. Electrically the bundle behaves as a parallel combination, so its effective resistivity sits between the two, around two per cent below plain OFC. At an inferred 14 AWG that puts loop resistance near 0.016 ohm/m and a 3 m pair at about 0.048 ohm, or 0.10 dB into 4 ohms.',
  }),

  makeCable({
    mfr: 'Zonotone',
    model: '6NSP-Granster 5500a',
    cableType: 'speaker',
    price: usd(750, 'APPROXIMATE', 2025, 'Approximate cost of a 3 m pair cut from bulk cable.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['bare', 'banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f7a4c',
    tags: ['bulk-cable', 'twisted', 'multi-metal-conductor'],
    provenanceNote:
      'Multi-metal conductor construction is published by the maker; the overall conductor section is inferred from the cable\'s diameter. No per-metre electrical figures are published.',
    notes:
      'A heavier multi-metal twisted pair from the Granster line. At an inferred 12 AWG the loop resistance is about 0.010 ohm/m, so 3 m of it contributes roughly 0.030 ohm, near 0.07 dB into 4 ohms. Mixing conductor metals changes the effective resistivity by a couple of per cent; the gauge step from the 2200a is worth roughly twenty times that.',
  }),

  makeCable({
    mfr: 'Zonotone',
    model: '6NSP-Granster 7700a',
    cableType: 'speaker',
    price: usd(1400, 'APPROXIMATE', 2025, 'Approximate cost of a 3 m pair cut from bulk cable.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 11,
    dielectric: 'Polyethylene',
    termination: ['bare', 'banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#356b41',
    tags: ['bulk-cable', 'twisted', 'multi-metal-conductor', 'upper-range'],
    provenanceNote:
      'Multi-metal conductor construction is published; the overall conductor section is inferred from cable diameter. Price is approximate and depends on where the cable is cut and how it is terminated.',
    notes:
      'The heaviest of the Granster speaker cables here, an inferred 11 AWG multi-metal bundle. Loop resistance around 0.008 ohm/m puts a 3 m pair at roughly 0.024 ohm, about 0.05 dB into 4 ohms. Below about 0.05 dB the level difference between two cables of the same length stops being something a level-matched listener can identify, which is worth knowing before spending further up a range.',
  }),

  makeCable({
    mfr: 'Zonotone',
    model: '6NAC-3000 Meister',
    cableType: 'interconnect',
    price: usd(320, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'copper-silver-hybrid',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper screen',
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4f8a5b',
    tags: ['screened', 'multi-metal-conductor'],
    provenanceNote:
      'Multi-metal conductor construction and the screened single-ended build are published; the conductor section is inferred from the cable diameter.',
    notes:
      'A screened RCA interconnect using the same multi-metal conductor idea as the speaker cables. Coaxial geometry near 85 pF/m means a 1 m pair sits around 85 pF per channel. On an interconnect the conductor metallurgy is close to irrelevant: the series resistance is a tenth of an ohm against an input impedance of tens of kilohms, and capacitance is what the engine actually computes with.',
  }),

  makeCable({
    mfr: 'Zonotone',
    model: '6NAC-Granster 5000a',
    cableType: 'interconnect',
    price: usd(750, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil and braid with a drain',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f7a4c',
    tags: ['screened', 'balanced', 'multi-metal-conductor'],
    provenanceNote:
      'Multi-metal conductors, the foil-and-braid screen and the balanced option are published; conductor size is inferred. No published per-metre electrical data.',
    notes:
      'The upper interconnect in the Granster line, offered single-ended or balanced. Twisted pair inside a foil-and-braid screen at roughly 70 pF/m: a 1 m link is about 70 pF, and the low-pass corner that forms against even a high-impedance valve output stage sits hundreds of kilohertz above the audio band.',
  }),

  makeCable({
    mfr: 'Zonotone',
    model: '6NPS-3.5 Meister',
    cableType: 'power',
    price: usd(320, 'APPROXIMATE', 2025, 'Approximate cost of a 1.8 m cord assembled from bulk cable.'),
    priceLengthM: 1.8,
    country: 'Japan',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Braided copper screen',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4f8a5b',
    tags: ['bulk-cable', 'mains-cord', 'multi-metal-conductor'],
    provenanceNote:
      'The 3.5 in the model designation is the published conductor section in square millimetres, which corresponds closely to 12 AWG. Sold as bulk cable, so the price is for an assembled cord.',
    notes:
      'Bulk three-core mains cable with 3.5 sq mm conductors -- about 12 AWG -- using the maker\'s multi-metal conductor bundle under a braided screen. Sized for a 15 A circuit and normally assembled with the buyer\'s choice of connectors. The engine applies no signal processing to mains cords.',
  }),

  makeCable({
    mfr: 'Zonotone',
    model: '6NPS-Neo Grandio 5.5Hi',
    cableType: 'power',
    price: usd(750, 'APPROXIMATE', 2025, 'Approximate cost of a 1.8 m cord assembled from bulk cable.'),
    priceLengthM: 1.8,
    country: 'Japan',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Braided copper screen over a semiconducting layer',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#356b41',
    tags: ['bulk-cable', 'mains-cord', 'multi-metal-conductor', 'high-current'],
    provenanceNote:
      'The 5.5 in the model designation is the published conductor section in square millimetres, near 10 AWG. Sold as bulk cable by the metre, so the price is for an assembled cord with mid-range connectors.',
    notes:
      'Heavy bulk mains cable with 5.5 sq mm conductors, close to 10 AWG, in the maker\'s multi-metal bundle under a screen and a semiconducting layer. That is a larger conductor than the fixed wiring in most domestic walls, and it will supply a large power amplifier with a drop of a few tens of millivolts over a typical cord length. Construction only: no processing is applied to power cords in this engine.',
  }),
];
